import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { noop } from 'lodash-es';

import Modal from 'reactstrap/lib/Modal';
import ModalHeader from 'reactstrap/lib/ModalHeader';
import ModalBody from 'reactstrap/lib/ModalBody';
import ModalFooter from 'reactstrap/lib/ModalFooter';
import Button from 'reactstrap/lib/Button';

import { useToggle } from 'site-modules/shared/hooks/use-toggle';
import edmundsLogo from 'client/images/edmunds-logo-226.png';
import { fireDeleteConfirmationOpen } from 'client/engagement-handlers/profile-engagement-handler/profile-engagement-handler';

import './delete-confirmation.scss';

export function DeleteConfirmation({
  buttonText,
  buttonClassName = null,
  buttonIcon = null,
  isSpinnerActive = false,
  preOpenCallback = () => Promise.resolve(),
  acceptCallback = noop,
  cancelCallback = noop,
}) {
  const [isModalOpen, toggleModalOpen] = useToggle(false);

  function toggleModal() {
    if (!isModalOpen) {
      fireDeleteConfirmationOpen();
    }
    toggleModalOpen();
  }

  function open() {
    preOpenCallback()
      .then(() => toggleModal())
      .catch(() => {});
  }

  function accept() {
    acceptCallback();
    toggleModal();
  }

  function cancel() {
    cancelCallback();
    toggleModal();
  }

  return (
    <Fragment>
      <Button
        color=""
        className={classnames('delete-confirmation submit-button', buttonClassName, {
          'spinner-active': isSpinnerActive,
        })}
        onClick={open}
        disabled={isSpinnerActive}
      >
        <i className="spinner icon-spinner8" aria-hidden />
        {buttonIcon && <i className={classnames('icon pointer-event-none ms-0_5', buttonIcon)} />}
        <span className="ps-0_5 mb-0 pointer-event-none">{buttonText}</span>
      </Button>
      <Modal
        className="delete-confirmation-modal d-flex align-items-center"
        contentClassName="py-1 px-0_5"
        backdrop="static"
        isOpen={isModalOpen}
        toggle={cancel}
      >
        <ModalHeader tag="div" className="text-gray-dark border-0">
          <img src={edmundsLogo} height={32} alt="Edmunds" className="mw-100" />
        </ModalHeader>
        <ModalBody>
          Are you sure you want to delete your account? Deleting your account is permanent and removes all data.
        </ModalBody>
        <ModalFooter className="border-0 justify-content-start">
          <Button color="primary" className="text-transform-none" aria-label="Yes" onClick={accept}>
            Yes, Delete
          </Button>
          <Button color="outline-primary" className="text-transform-none" aria-label="No" onClick={cancel}>
            No, Keep my account
          </Button>
        </ModalFooter>
      </Modal>
    </Fragment>
  );
}

DeleteConfirmation.propTypes = {
  buttonText: PropTypes.string.isRequired,
  buttonClassName: PropTypes.string,
  buttonIcon: PropTypes.string,
  preOpenCallback: PropTypes.func,
  acceptCallback: PropTypes.func,
  cancelCallback: PropTypes.func,
  isSpinnerActive: PropTypes.bool,
};
